// =============================================================================
// rate-collection-client.http.ts — Adaptateur HTTP RÉEL du RateCollectionClient (S12, D2).
// =============================================================================
// Câble la SPA contre les endpoints FOURNISSEUR du BC rate-collection (contrat de routes
// GELÉ D1). Substitué derrière le MÊME DI seam (RateCollectionClientProvider) : écrans/hooks
// consomment l'interface RateCollectionClient, l'impl est substituée sans toucher l'UI.
//
// SÉCURITÉ (RULE_REF_006) : le secret de session fournisseur (cookie httpOnly `supplier_session`)
// n'est JAMAIS lu ni fabriqué ici. La session est portée par le cookie (credentials: 'include').
// Le siret + le contact viennent de la session côté API (jamais du body). AUCUNE donnée métier
// en localStorage : la résilience repose sur la BASE (re-fetch du Brouillon persisté), pas sur
// un cache local.
//
// CSRF DOUBLE-SUBMIT FOURNISSEUR (TenantInterceptor, supplier-cookies.ts) : sur les mutations
// (PUT/POST), l'en-tête `x-csrf-token` DOIT égaler le cookie NON-httpOnly `supplier_csrf`.
// ⚠️ DIVERGENCE ASSUMÉE vs admin : le cookie CSRF FOURNISSEUR est `supplier_csrf` (admin =
// `admin_csrf`). Même header, même patron double-submit ; SEUL le nom de cookie change. Sans
// l'en-tête, toute écriture fournisseur est 403 (fail-closed serveur, jamais d'écriture).
//
// MAPPING ERREURS : le 409 fournisseur est un ConflictException NestJS NON structuré
// ({ statusCode, message, error }) — PAS de champ `code` machine (contraste avec campaign).
// On mappe donc par STATUS HTTP (409 → already-submitted, 400 → invalid-value, 403 →
// forbidden, 401 → unauthorized, 404 → not-found) et on relaie le message serveur tel quel.

import {
  RateCollectionApiError,
  type AutosaveAckDto,
  type AutosaveCellInput,
  type CloneSubmissionInput,
  type CloneSubmissionResultDto,
  type DeclareSkillInput,
  type DeclaredSkillDto,
  type DeclaredSkillListDto,
  type EditLockAckDto,
  type EditLockReleaseAckDto,
  type GrilleViewDto,
  type PriorCampaignListDto,
  type ProfileDetailDto,
  type RateCollectionApiErrorKind,
  type RateCollectionClient,
  type RateSeniorityLevelDto,
  type ReopenSubmissionAckDto,
  type SkillReferentialByProfileListDto,
  type SubmitAckDto,
} from '@/api/rate-collection-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  /** S13 : code machine STABLE du 409 (corps `{statusCode, code, message}`, D1 §3). */
  readonly code?: string;
  readonly message?: string | readonly string[];
  readonly error?: string;
  /** A0-11 (DBU l.2948) : NOM du détenteur d'un verrou concurrent (CELL_LOCKED), champ structuré PII. */
  readonly holderDisplayName?: string | null;
}

/** Nom du cookie CSRF NON-httpOnly FOURNISSEUR (cf. API supplier-cookies.ts SUPPLIER_CSRF_COOKIE). */
const SUPPLIER_CSRF_COOKIE_NAME = 'supplier_csrf';

/**
 * TIMEOUT CLIENT BORNÉ de toute requête fournisseur (RÉSILIENCE, ST-SAISIE-02 / décision
 * GATE_HUMAN John 2026-06-14 « Fix prod : timeout autosave »). Sans borne, une coupure réseau
 * (paquets perdus, pas de RST) laisse `fetch` PENDANT indéfiniment : l'autosave reste « en cours »
 * pour toujours au lieu de basculer en « échec, saisie conservée » + retry. On enveloppe donc
 * CHAQUE requête (getGrille/autosaveCell/submit, toutes via request()) d'un AbortController armé à
 * 3 s : à l'expiration, `fetch` jette une AbortError (DOMException). Elle se propage hors de
 * request() et N'EST PAS une RateCollectionApiError ⇒ le catch du hook d'autosave tombe sur sa
 * branche réseau générique (« Échec…La saisie est conservée. ») — comportement FIGÉ voulu.
 *
 * VALEUR = 3000 ms (TRANCHÉ GATE_HUMAN John 2026-06-14) : SOUS la fenêtre d'assertion test (5 s,
 * spec FIGÉ inchangé), avec marge pour le catch + le rendu de l'indicateur ; réactif côté
 * fournisseur ; pas si court qu'il avorterait un enregistrement lent mais légitime.
 */
const AUTOSAVE_REQUEST_TIMEOUT_MS = 3000;

/**
 * Mapping STATUS HTTP → kind UX. Le 409 S12 (autosave/submit déjà-soumis) reste NON structuré
 * (mappé ici par status → 'already-submitted'). Le 409 S13 EST structuré (`code` machine) : il est
 * discriminé EN AMONT par CODE_TO_KIND (cf. toApiError), avec ce mapping en repli.
 */
const STATUS_TO_KIND: Readonly<Record<number, RateCollectionApiErrorKind>> = {
  400: 'invalid-value',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
  409: 'already-submitted',
};

/**
 * S13 — discriminateur 409 par CODE machine STABLE (corps `{statusCode, code, message}`, D1 §3).
 * Pris en compte AVANT le mapping par status : un code inconnu retombe sur STATUS_TO_KIND (donc
 * 'already-submitted' pour un 409 sans code reconnu — non-régression S12). Table FIGÉE (D2 contre D1).
 */
const CODE_TO_KIND: Readonly<Record<string, RateCollectionApiErrorKind>> = {
  SUBMISSION_DEADLINE_PASSED: 'deadline-passed',
  CELL_LOCKED: 'cell-locked',
  CLONE_NO_PRIOR_PARTICIPATION: 'clone-no-prior-participation',
  SUBMISSION_NOT_REOPENABLE: 'not-reopenable',
};

/**
 * Lit la valeur du cookie CSRF fournisseur `supplier_csrf` (NON-httpOnly, lisible par le SPA
 * même origine). `undefined` si absent (pas de session, ou contexte sans `document`) — l'appel
 * mutant partira alors sans en-tête et l'API répondra 403 (fail-closed serveur).
 */
function readSupplierCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === SUPPLIER_CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : appels `fetch` contre l'API fournisseur rate-collection. Le `baseUrl`
 * par défaut est vide (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy
 * route `/supplier/*`). En test, on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpRateCollectionClient implements RateCollectionClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Exécute une requête et renvoie le JSON typé. Sur les méthodes MUTANTES (PUT/POST), pose
   * l'en-tête CSRF `x-csrf-token` = cookie `supplier_csrf`. En cas d'erreur HTTP, lève une
   * RateCollectionApiError (kind mappé par STATUS, message serveur relayé tel quel).
   */
  private async request<T>(
    path: string,
    init: RequestInit,
    expectBody: boolean,
  ): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readSupplierCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }

    // TIMEOUT BORNÉ : AbortController armé à AUTOSAVE_REQUEST_TIMEOUT_MS. À l'abort, `fetch` jette
    // une AbortError (DOMException) qui se propage (PAS de conversion en RateCollectionApiError :
    // le hook d'autosave doit la traiter comme un échec réseau générique). clearTimeout en `finally`
    // (succès comme échec) ⇒ aucune fuite de timer.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, AUTOSAVE_REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(this.url(path), {
        // Session fournisseur = cookie httpOnly `supplier_session` : transmis avec credentials.
        credentials: 'include',
        ...init,
        // `signal` APRÈS `...init` : le timeout client s'applique TOUJOURS (aucun appelant ne passe
        // de signal ; on garantit néanmoins que la borne ne peut être écrasée par `init`).
        signal: controller.signal,
        headers: { ...headers, ...init.headers },
      });

      if (!response.ok) {
        throw await this.toApiError(response);
      }
      if (!expectBody) {
        return undefined as T;
      }
      return (await response.json()) as T;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Convertit une réponse d'erreur en RateCollectionApiError typée. S13 : si le corps porte un
   * `code` machine reconnu (CODE_TO_KIND), il PRIME (discriminateur stable, ex. CELL_LOCKED,
   * SUBMISSION_DEADLINE_PASSED) ; sinon repli sur le mapping par STATUS (non-régression S12).
   */
  private async toApiError(response: Response): Promise<RateCollectionApiError> {
    const body = await this.safeJson(response);
    const message = this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const byCode =
      typeof body?.code === 'string' ? CODE_TO_KIND[body.code] : undefined;
    const kind = byCode ?? STATUS_TO_KIND[response.status] ?? 'generic';
    // A0-11 (DBU l.2948) : propage le NOM du détenteur du verrou concurrent (badge). `null` si non résolu.
    const holderDisplayName =
      typeof body?.holderDisplayName === 'string' ? body.holderDisplayName : null;
    return new RateCollectionApiError(message, kind, holderDisplayName);
  }

  /** Aplatit le `message` NestJS (string ou string[]) en une phrase. */
  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  /** Parse le corps JSON sans jeter (réponses d'erreur non-JSON tolérées). */
  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }

  public async getGrille(submissionId: string): Promise<GrilleViewDto> {
    return this.request<GrilleViewDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/grille`,
      { method: 'GET' },
      true,
    );
  }

  public async autosaveCell(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
    input: AutosaveCellInput,
  ): Promise<AutosaveAckDto> {
    return this.request<AutosaveAckDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/cells/${encodeURIComponent(
        profileCode,
      )}/${encodeURIComponent(seniorityLevel)}`,
      { method: 'PUT', body: JSON.stringify(input) },
      true,
    );
  }

  public async submit(submissionId: string): Promise<SubmitAckDto> {
    // Corps VIDE : le contact soumetteur est résolu de la session (invariant n°1).
    return this.request<SubmitAckDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/submit`,
      { method: 'POST', body: JSON.stringify({}) },
      true,
    );
  }

  public async reopenSubmission(
    submissionId: string,
  ): Promise<ReopenSubmissionAckDto> {
    // Corps VIDE : le siret/contact sont résolus de la session (invariant n°1). A0-10 (DBU §3.5).
    return this.request<ReopenSubmissionAckDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/reopen`,
      { method: 'POST', body: JSON.stringify({}) },
      true,
    );
  }

  // --- S13 (A) Clonage par copie inter-campagne -------------------------------

  public async cloneSubmission(
    submissionId: string,
    input: CloneSubmissionInput,
  ): Promise<CloneSubmissionResultDto> {
    return this.request<CloneSubmissionResultDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/clone`,
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  }

  // --- S13 (D4 SEAM) Lectures front : clonage (F1) + référentiel compétences (F2) -

  public async getPriorCampaigns(
    submissionId: string,
  ): Promise<PriorCampaignListDto> {
    return this.request<PriorCampaignListDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/prior-campaigns`,
      { method: 'GET' },
      true,
    );
  }

  public async getSkillReferential(
    submissionId: string,
  ): Promise<SkillReferentialByProfileListDto> {
    return this.request<SkillReferentialByProfileListDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/skill-referential`,
      { method: 'GET' },
      true,
    );
  }

  public async getProfileDetail(
    submissionId: string,
    profileCode: string,
  ): Promise<ProfileDetailDto> {
    return this.request<ProfileDetailDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/profiles/${encodeURIComponent(
        profileCode,
      )}`,
      { method: 'GET' },
      true,
    );
  }

  // --- S13 (B) EditLock Postgres, granularité cellule -------------------------

  /** Chemin du verrou d'une cellule (sans le suffixe d'action). */
  private lockPath(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
  ): string {
    return `/supplier/submissions/${encodeURIComponent(submissionId)}/cells/${encodeURIComponent(
      profileCode,
    )}/${encodeURIComponent(seniorityLevel)}/lock`;
  }

  public async acquireLock(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
  ): Promise<EditLockAckDto> {
    return this.request<EditLockAckDto>(
      this.lockPath(submissionId, profileCode, seniorityLevel),
      { method: 'POST', body: JSON.stringify({}) },
      true,
    );
  }

  public async releaseLock(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
  ): Promise<EditLockReleaseAckDto> {
    return this.request<EditLockReleaseAckDto>(
      this.lockPath(submissionId, profileCode, seniorityLevel),
      { method: 'DELETE' },
      true,
    );
  }

  public async heartbeatLock(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
  ): Promise<EditLockAckDto> {
    return this.request<EditLockAckDto>(
      `${this.lockPath(submissionId, profileCode, seniorityLevel)}/heartbeat`,
      { method: 'PUT', body: JSON.stringify({}) },
      true,
    );
  }

  // --- S13 (D) Déclaration de compétences, granularité PROFIL -----------------

  public async listSkills(submissionId: string): Promise<DeclaredSkillListDto> {
    return this.request<DeclaredSkillListDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/skills`,
      { method: 'GET' },
      true,
    );
  }

  public async declareSkill(
    submissionId: string,
    input: DeclareSkillInput,
  ): Promise<DeclaredSkillDto> {
    return this.request<DeclaredSkillDto>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/skills`,
      { method: 'PUT', body: JSON.stringify(input) },
      true,
    );
  }

  public async removeSkill(
    submissionId: string,
    profileCode: string,
    skillCode: string,
  ): Promise<{ readonly removed: boolean }> {
    return this.request<{ readonly removed: boolean }>(
      `/supplier/submissions/${encodeURIComponent(submissionId)}/skills/${encodeURIComponent(
        profileCode,
      )}/${encodeURIComponent(skillCode)}`,
      { method: 'DELETE' },
      true,
    );
  }
}
